import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UserHistoryTab } from "@/components/UserHistoryTab";
import { UserReportModal } from "@/components/UserReportModal";
import { useToast } from "@/hooks/use-toast";
import { useLang } from "@/contexts/LangContext";
import { tr } from "@/lib/i18n";
import {
  Pencil,
  Trash2,
  Users as UsersIcon,
  Star,
  Search,
  Key,
  Mail,
  LockOpen,
  Lock,
  Download,
  History as HistoryIcon,
  User as UserIcon,
  Calendar,
  BarChart3,
} from "lucide-react";

import { getApiBaseUrl, adminFetch, unlockUser } from "@/lib/api";
import { cn } from "@/lib/utils";

const API_BASE = getApiBaseUrl();

interface User {
  id: string;
  email: string;
  name: string;
  isPremium: string;
  provider: string;
  avatar: string | null;
  planId: number | null;
  hasPassword: boolean;
  isLocked: boolean;
  lockedUntil: string | null;
  failedLoginAttempts: number;
  createdAt: string;
}

interface Plan { id: number; name: string; nameEn: string; dailyLimit: number; }

async function fetchUsers(search: string): Promise<User[]> {
  const url = search
    ? `${API_BASE}/api/users?search=${encodeURIComponent(search)}`
    : `${API_BASE}/api/users`;
  const res = await adminFetch(url);
  if (!res.ok) throw new Error("Failed to fetch users");
  return res.json();
}
async function fetchPlans(): Promise<Plan[]> {
  const res = await adminFetch(`${API_BASE}/api/plans`);
  if (!res.ok) throw new Error("Failed to fetch plans");
  return res.json();
}
async function updateUser(id: string, data: Partial<Pick<User, "name" | "email" | "isPremium">>): Promise<User> {
  const res = await adminFetch(`${API_BASE}/api/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update user");
  return res.json();
}
async function enrollPlan(id: string, planId: number | null): Promise<User> {
  const res = await adminFetch(`${API_BASE}/api/users/${id}/plan`, {
    method: "POST",
    body: JSON.stringify({ planId }),
  });
  if (!res.ok) throw new Error("Failed to update plan");
  return res.json();
}
async function resetPassword(id: string, password: string): Promise<void> {
  const res = await adminFetch(`${API_BASE}/api/users/${id}/reset-password`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Failed to reset password");
  }
}

async function deleteUser(id: string): Promise<void> {
  const res = await adminFetch(`${API_BASE}/api/users/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete user");
}

function formatLockedUntil(lockedUntil: string, lang: "ar" | "en"): string {
  const date = new Date(lockedUntil);
  const secsLeft = Math.ceil((date.getTime() - Date.now()) / 1000);
  if (secsLeft <= 0) return lang === "ar" ? "تنتهي قريباً" : "Expiring…";
  if (secsLeft < 60) return lang === "ar" ? `${secsLeft}ث` : `${secsLeft}s`;
  const mins = Math.ceil(secsLeft / 60);
  return lang === "ar" ? `${mins} دقيقة` : `${mins}m`;
}

const NO_PLAN = "none";

export default function Users() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { lang } = useLang();
  const [search, setSearch] = useState("");
  const [editUser, setEditUser] = useState<User | null>(null);
  const [reportUser, setReportUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [form, setForm] = useState({ name: "", email: "", isPremium: "false", planId: NO_PLAN });
  const [newPassword, setNewPassword] = useState("");

  const { data: users = [], isLoading } = useQuery({ queryKey: ["users", search], queryFn: () => fetchUsers(search) });
  const { data: plans = [] } = useQuery({ queryKey: ["plans"], queryFn: fetchPlans });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!editUser) return;
      await updateUser(editUser.id, { name: form.name, email: form.email, isPremium: form.isPremium });
      const desiredPlan = form.planId === NO_PLAN ? null : Number(form.planId);
      if (desiredPlan !== (editUser.planId ?? null)) await enrollPlan(editUser.id, desiredPlan);
      if (newPassword.trim()) await resetPassword(editUser.id, newPassword.trim());
    },
    onSuccess: () => { invalidate(); setEditUser(null); setNewPassword(""); toast({ title: tr(lang, "userUpdated") }); },
    onError: (e) => toast({ title: e instanceof Error ? e.message : "Error", variant: "destructive" }),
  });

  const unlockMut = useMutation({
    mutationFn: (id: string) => unlockUser(id),
    onSuccess: (updated) => {
      qc.setQueryData(["users", search], (old: User[] | undefined) =>
        old ? old.map((u) => (u.id === updated.id ? updated : u)) : old
      );
      if (editUser?.id === updated.id) setEditUser(updated);
      invalidate();
      toast({
        title: tr(lang, "userUnlocked"),
        description:
          lang === "ar"
            ? "تم تصفير المحاولات الفاشلة وفتح الحساب بنجاح."
            : "Failed login attempts reset and account unlocked successfully.",
      });
    },
    onError: (e) =>
      toast({
        title: lang === "ar" ? "فشل فتح القفل" : "Unlock Failed",
        description: e instanceof Error ? e.message : (lang === "ar" ? "حدث خطأ أثناء فتح القفل" : "An error occurred while unlocking"),
        variant: "destructive",
      }),
  });

  const deleteMut = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => { invalidate(); toast({ title: tr(lang, "userDeleted") }); },
    onError: () => toast({ title: "Error", variant: "destructive" }),
  });

  const [isExporting, setIsExporting] = useState(false);

  const handleExportCsv = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/export/users`);
      if (!res.ok) throw new Error("Failed to export users CSV");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `tayyibati_users_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: tr(lang, "exportSuccess"),
        description: tr(lang, "exportUsersDesc"),
      });
    } catch (err: any) {
      toast({
        title: tr(lang, "exportFailed"),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const openEdit = (user: User, initialTab: string = "overview") => {
    setEditUser(user);
    setActiveTab(initialTab);
    setForm({ name: user.name, email: user.email, isPremium: user.isPremium, planId: user.planId != null ? String(user.planId) : NO_PLAN });
    setNewPassword("");
  };

  const handleDelete = (user: User) => {
    const msg = lang === "ar"
      ? `حذف المستخدم "${user.email}" نهائياً؟`
      : `Delete user "${user.email}"? This cannot be undone.`;
    if (confirm(msg)) deleteMut.mutate(user.id);
  };

  const planName = (id: number | null) => {
    if (id == null) return null;
    const p = plans.find((pl) => pl.id === id);
    if (!p) return `#${id}`;
    return lang === "ar" ? p.name : p.nameEn;
  };

  return (
    <div className="p-6 space-y-6" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{tr(lang, "users")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {lang === "ar" ? "إدارة الحسابات والخطط والصلاحيات" : "Manage app accounts, plans, and access"}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={handleExportCsv} disabled={isExporting} className="gap-1.5 text-xs">
            <Download className="h-4 w-4" />
            {isExporting ? tr(lang, "exporting") : tr(lang, "exportCSV")}
          </Button>
          <div className="relative w-full sm:w-72">
            <Search
              className={cn(
                "absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none",
                lang === "ar" ? "right-3" : "left-3"
              )}
            />
            <Input
              className={cn("h-9 text-xs", lang === "ar" ? "pr-9 pl-3 text-right" : "pl-9 pr-3 text-left")}
              placeholder={lang === "ar" ? "بحث بالاسم أو البريد..." : "Search by name or email..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground text-sm py-8 text-center">{tr(lang, "loading")}</div>
      ) : users.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <UsersIcon className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">
            {search
              ? (lang === "ar" ? "لا يوجد مستخدمون يطابقون البحث" : "No users match your search.")
              : tr(lang, "noUsers")}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-xs overflow-x-auto">
          <Table className="min-w-[880px] w-full">
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead className="min-w-[240px] text-start font-semibold text-xs py-3.5 px-4">
                  {lang === "ar" ? "المستخدم" : "User"}
                </TableHead>
                <TableHead className="w-[120px] text-start font-semibold text-xs py-3.5 px-4">
                  {tr(lang, "provider")}
                </TableHead>
                <TableHead className="w-[140px] text-start font-semibold text-xs py-3.5 px-4">
                  {tr(lang, "plan")}
                </TableHead>
                <TableHead className="w-[120px] text-start font-semibold text-xs py-3.5 px-4">
                  {tr(lang, "status")}
                </TableHead>
                <TableHead className="min-w-[180px] text-start font-semibold text-xs py-3.5 px-4">
                  {lang === "ar" ? "الحجب الأمني" : "Lockout"}
                </TableHead>
                <TableHead className="w-[160px] text-center font-semibold text-xs py-3.5 px-4">
                  {tr(lang, "actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className="hover:bg-muted/30 transition-colors">
                  <TableCell className="py-3 px-4 text-start">
                    <div className="font-semibold text-foreground text-sm">{user.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 font-mono">{user.email}</div>
                  </TableCell>
                  <TableCell className="py-3 px-4 text-start">
                    <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-md bg-muted text-muted-foreground capitalize">
                      {user.provider}
                    </span>
                  </TableCell>
                  <TableCell className="py-3 px-4 text-start">
                    <span className="text-xs font-medium text-foreground">
                      {planName(user.planId) ?? <span className="text-muted-foreground">—</span>}
                    </span>
                  </TableCell>
                  <TableCell className="py-3 px-4 text-start">
                    {user.isPremium === "true" ? (
                      <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-xs">
                        <Star className="h-3 w-3" /> {tr(lang, "premium")}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">{tr(lang, "basic")}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="py-3 px-4 text-start">
                    {user.isLocked ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge variant="destructive" className="gap-1 text-xs">
                          <Lock className="h-3 w-3" /> {tr(lang, "locked")}
                        </Badge>
                        {user.lockedUntil && (
                          <span className="text-xs text-muted-foreground">
                            {formatLockedUntil(user.lockedUntil, lang)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {user.isLocked && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => unlockMut.mutate(user.id)}
                          disabled={unlockMut.isPending}
                          title={tr(lang, "unlock")}
                          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <LockOpen className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setReportUser(user)}
                        title={tr(lang, "userReport")}
                        className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"
                      >
                        <BarChart3 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(user, "history")}
                        title={lang === "ar" ? "سجل النشاط" : "Activity History"}
                        className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/10"
                      >
                        <HistoryIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(user, "overview")}
                        title={tr(lang, "edit")}
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(user)}
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        title={tr(lang, "delete")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Redesigned User Details Modal */}
      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent
          className="sm:max-w-3xl max-h-[92vh] flex flex-col p-0 overflow-hidden shadow-2xl [&>button:last-child]:rtl:left-4 [&>button:last-child]:rtl:right-auto"
          dir={lang === "ar" ? "rtl" : "ltr"}
        >
          {/* Fixed Dialog Header */}
          <DialogHeader className="p-6 pb-4 border-b bg-card shrink-0">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3.5 min-w-0">
                <div className="h-12 w-12 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 flex items-center justify-center font-bold text-lg shrink-0">
                  {editUser?.name ? editUser.name.charAt(0).toUpperCase() : editUser?.email.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-xl font-bold text-foreground truncate">
                    {editUser?.name || editUser?.email}
                  </DialogTitle>
                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span className="font-mono text-muted-foreground/80">{editUser?.email}</span>
                    <span>·</span>
                    <span>
                      {lang === "ar" ? "انضم في" : "Joined"}{" "}
                      {editUser?.createdAt
                        ? new Date(editUser.createdAt).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                          })
                        : "—"}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReportUser(editUser)}
                  className="gap-1.5 text-xs text-primary hover:text-primary hover:bg-primary/10 border-primary/20"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  {tr(lang, "userReport")}
                </Button>
                {editUser?.isPremium === "true" ? (
                  <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-700">
                    <Star className="h-3 w-3" /> {tr(lang, "premium")}
                  </Badge>
                ) : (
                  <Badge variant="secondary">{tr(lang, "basic")}</Badge>
                )}
                {editUser?.isLocked && (
                  <Badge variant="destructive" className="gap-1">
                    <Lock className="h-3 w-3" /> {tr(lang, "locked")}
                  </Badge>
                )}
              </div>
            </div>
          </DialogHeader>

          {editUser && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
              <div className="px-6 pt-3.5 shrink-0 bg-muted/20">
                <TabsList className="grid w-full grid-cols-3 bg-muted/60 p-1 rounded-lg">
                  <TabsTrigger value="overview" className="gap-1.5 text-xs font-medium">
                    <UserIcon className="h-3.5 w-3.5" />
                    {tr(lang, "overviewTab")}
                  </TabsTrigger>
                  <TabsTrigger value="subscription" className="gap-1.5 text-xs font-medium">
                    <Star className="h-3.5 w-3.5" />
                    {tr(lang, "subscriptionTab")}
                  </TabsTrigger>
                  <TabsTrigger value="history" className="gap-1.5 text-xs font-medium">
                    <HistoryIcon className="h-3.5 w-3.5" />
                    {tr(lang, "historyTab")}
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Scrollable Modal Content Container */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                {/* OVERVIEW TAB */}
                <TabsContent value="overview" className="space-y-4 m-0">
                  {editUser.isLocked ? (
                    <div className="flex items-center justify-between rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <Lock className="h-4 w-4 text-destructive shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-destructive">
                            {lang === "ar" ? "الحساب محجوب مؤقتاً" : "Account temporarily locked"}
                          </p>
                          {editUser.lockedUntil && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {formatLockedUntil(editUser.lockedUntil, lang)} · {editUser.failedLoginAttempts} {tr(lang, "failedAttempts")}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive text-xs"
                        onClick={() => unlockMut.mutate(editUser.id)}
                        disabled={unlockMut.isPending}
                      >
                        <LockOpen className="h-3.5 w-3.5" />
                        {tr(lang, "unlock")}
                      </Button>
                    </div>
                  ) : (editUser.failedLoginAttempts ?? 0) > 0 ? (
                    <div className="flex items-center justify-between rounded-lg border border-amber-300/40 bg-amber-500/5 px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <LockOpen className="h-4 w-4 text-amber-600 shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-amber-600">
                            {lang === "ar" ? "محاولات دخول فاشلة مسجلة" : "Recorded failed login attempts"}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {editUser.failedLoginAttempts} / 10 {tr(lang, "failedAttempts")}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 border-amber-500/40 text-amber-600 hover:bg-amber-500/10 text-xs"
                        onClick={() => unlockMut.mutate(editUser.id)}
                        disabled={unlockMut.isPending}
                      >
                        <LockOpen className="h-3.5 w-3.5" />
                        {lang === "ar" ? "تصفير المحاولات" : "Reset attempts"}
                      </Button>
                    </div>
                  ) : null}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-foreground">{tr(lang, "name")}</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-foreground flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                        {tr(lang, "email")}
                      </Label>
                      <Input
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="h-9 font-mono text-xs"
                      />
                    </div>
                  </div>

                  <div className="rounded-lg border p-4 space-y-2 bg-muted/10">
                    <Label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                      <Key className="h-3.5 w-3.5 text-primary" />
                      {tr(lang, "resetPassword")}
                    </Label>
                    <Input
                      type="text"
                      placeholder={lang === "ar" ? "اتركه فارغاً للإبقاء على كلمة المرور الحالية" : "Leave blank to keep current"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="h-9"
                    />
                    <p className="text-xs text-muted-foreground leading-normal">
                      {editUser?.hasPassword
                        ? (lang === "ar" ? "المستخدم لديه كلمة مرور مسجلة." : "User has an existing password.")
                        : (lang === "ar" ? "لا توجد كلمة مرور مسجلة لهذا المستخدم (قد يكون مسجلاً عبر Google)." : "User has no password set (may have signed in with Google).")}
                      {" "}{lang === "ar" ? "يجب أن تكون كلمة المرور 4 أحرف على الأقل." : "Password must be at least 4 characters."}
                    </p>
                  </div>
                </TabsContent>

                {/* SUBSCRIPTION TAB */}
                <TabsContent value="subscription" className="space-y-4 m-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-foreground">{tr(lang, "status")}</Label>
                      <Select value={form.isPremium} onValueChange={(v) => setForm({ ...form, isPremium: v })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="false">{tr(lang, "basic")}</SelectItem>
                          <SelectItem value="true">{tr(lang, "premium")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-foreground">{tr(lang, "plan")}</Label>
                      <Select value={form.planId} onValueChange={(v) => setForm({ ...form, planId: v })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_PLAN}>{lang === "ar" ? "بدون خطة" : "No plan"}</SelectItem>
                          {plans.map((p) => (
                            <SelectItem key={p.id} value={String(p.id)}>{lang === "ar" ? p.name : p.nameEn}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {form.planId !== NO_PLAN && (
                    <div className="rounded-lg border bg-muted/30 p-3.5 space-y-1 text-xs">
                      <div className="font-semibold text-sm text-foreground">
                        {planName(Number(form.planId))}
                      </div>
                      <p className="text-muted-foreground leading-normal">
                        {lang === "ar"
                          ? "حدود الباقة والمميزات تُطبق على هذا الحساب وتُسجل في سجل النشاط."
                          : "Plan limits and features are applied to this account and recorded in history."}
                      </p>
                    </div>
                  )}
                </TabsContent>

                {/* HISTORY TAB */}
                <TabsContent value="history" className="m-0">
                  <UserHistoryTab
                    userId={editUser.id}
                    userName={editUser.name}
                    userEmail={editUser.email}
                    isActive={activeTab === "history"}
                  />
                </TabsContent>
              </div>
            </Tabs>
          )}

          {/* Fixed Dialog Footer */}
          <DialogFooter className="p-4 px-6 border-t bg-card/80 backdrop-blur-xs flex items-center justify-between sm:justify-between w-full shrink-0">
            <span className="text-xs text-muted-foreground font-mono">
              ID: {editUser?.id}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditUser(null)} className="text-xs">
                {activeTab === "history" ? (lang === "ar" ? "إغلاق" : "Close") : tr(lang, "cancel")}
              </Button>
              {activeTab !== "history" && (
                <Button size="sm" onClick={() => saveMut.mutate()} disabled={!form.email || saveMut.isPending} className="text-xs">
                  {tr(lang, "save")}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Standalone User Report Modal */}
      <UserReportModal
        userId={reportUser?.id ?? null}
        userName={reportUser?.name ?? undefined}
        isOpen={!!reportUser}
        onClose={() => setReportUser(null)}
        defaultLang={lang}
      />
    </div>
  );
}
